"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * UserActionsContext — survives the SceneRouter's case-statement
 * unmount/remount cycle, so a CTA clicked on screen 4.5 ("Begin LC
 * composition") stays "fired" when the audience steps to 4.6 and the
 * old screen unmounts. Without this, every CTA-gate reset to its
 * pre-click state on remount and Shift+N rewind re-hit the same wall.
 *
 * The set of fired actions is persisted to sessionStorage so a soft
 * reload mid-demo (or the presenter re-opening the demo tab) restores
 * state. A scenario-level reset (e.g. starting a different scenario)
 * should clear sessionStorage — wired via `clear()` on the consumer.
 *
 * Action keys are free-form strings — recommended convention:
 *   "scope.Action"
 *   examples: "t1.SignContract", "t2.BeginLcComposition",
 *             "t2.PayWithOrbId", "t2.SignInvoice"
 */
type UserActionsContextValue = {
  has: (key: string) => boolean;
  fire: (key: string) => void;
  clear: () => void;
};

const UserActionsContext = createContext<UserActionsContextValue | null>(null);

export const STORAGE_KEY = "orbitra-demo.userActions";

/**
 * Pure helpers — extracted so the persistence + idempotence contract is
 * unit-testable without a React renderer. The React provider just wires
 * these to useState + sessionStorage.
 */
export function appendUserAction(prev: ReadonlySet<string>, key: string): Set<string> {
  if (prev.has(key)) return prev as Set<string>;
  const next = new Set(prev);
  next.add(key);
  return next;
}

export function serializeUserActions(set: ReadonlySet<string>): string {
  return JSON.stringify([...set]);
}

export function deserializeUserActions(raw: string | null | undefined): Set<string> {
  if (!raw) return new Set();
  try {
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter((v): v is string => typeof v === "string"));
  } catch {
    return new Set();
  }
}

export function UserActionsProvider({ children }: { children: React.ReactNode }) {
  const [fired, setFired] = useState<Set<string>>(() => new Set());

  // Restore from sessionStorage on mount (client-only).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const restored = deserializeUserActions(window.sessionStorage.getItem(STORAGE_KEY));
    if (restored.size > 0) setFired(restored);
  }, []);

  const has = useCallback((key: string) => fired.has(key), [fired]);

  const fire = useCallback((key: string) => {
    setFired((prev) => {
      const next = appendUserAction(prev, key);
      if (next === prev) return prev;
      try {
        window.sessionStorage.setItem(STORAGE_KEY, serializeUserActions(next));
      } catch {
        // ignore storage failures
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setFired(new Set());
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo(() => ({ has, fire, clear }), [has, fire, clear]);

  return (
    <UserActionsContext.Provider value={value}>
      {children}
    </UserActionsContext.Provider>
  );
}

export function useUserActions(): UserActionsContextValue {
  const ctx = useContext(UserActionsContext);
  if (!ctx) {
    throw new Error("useUserActions must be used inside <UserActionsProvider>");
  }
  return ctx;
}

/**
 * Action-key convention — kept here as the canonical list so component
 * call sites don't drift on string literals.
 */
export const USER_ACTION = {
  T1_SIGN_CONTRACT:           "t1.SignContract",
  T1_SIGN_INVOICE:            "t1.SignInvoice",
  T2_SIGN_CONTRACT:           "t2.SignContract",
  T2_SIGN_INVOICE:            "t2.SignInvoice",
  T2_BEGIN_LC_COMPOSITION:    "t2.BeginLcComposition",
  T2_PAY_LC_PLACEMENT_FEE:    "t2.PayLcPlacementFee",
  T2_PAY_WITH_ORBID:          "t2.PayWithOrbId",
  T2_VIEW_LC_ISSUED:          "t2.ViewLcIssued",
  T3_SIGN_CONTRACT:           "t3.SignContract",
  T3_SIGN_INVOICE:            "t3.SignInvoice",
  T3_BEGIN_LC_COMPOSITION:    "t3.BeginLcComposition",
  T3_PAY_LC_PLACEMENT_FEE:    "t3.PayLcPlacementFee",
  T3_VIEW_LC_ISSUED:          "t3.ViewLcIssued",
  T4_SIGN_CONTRACT:           "t4.SignContract",
  T4_SIGN_INVOICE:            "t4.SignInvoice",
  T4_BEGIN_LC_COMPOSITION:    "t4.BeginLcComposition",
  T4_PAY_LC_PLACEMENT_FEE:    "t4.PayLcPlacementFee",
  T4_VIEW_LC_ISSUED:          "t4.ViewLcIssued",
} as const;
